// Pilot behaviour: initial calls and requests, read-backs (occasionally wrong – the controller has to hear it),
// reaction times, frequency changes, repeated calls when nobody answers, standard actions (holding at the
// clearance limit, going around without landing clearance) and the execution of instructions.
import type { Aircraft } from './aircraft';
import type { Cmd } from './commands';
import type { World } from './world';
import type { HoldPoint } from './airport';
import { NM, dist, norm360 } from './geo';
import { distOnFinal } from './aircraft';
import { phrase as P, pilotCall, readbackTransmission, type Phrase } from '../phraseology/messages';
import { showLevel, sayLevel, sayRunway, spell, ALPHABET } from '../phraseology/spoken';

export type Stage =
  | 'PRE' | 'CLEARED' | 'PUSH_REQ' | 'PUSHING' | 'STARTING' | 'TAXI_REQ' | 'TAXIING' | 'AT_HOLD' | 'LINEUP' | 'TAKEOFF'
  | 'CLIMB' | 'ENROUTE' | 'ARRIVING' | 'FINAL' | 'LANDED' | 'TAXI_IN' | 'PARKED' | 'GONE';

export type RequestKind =
  | 'CLEARANCE' | 'PUSH' | 'STARTUP' | 'TAXI' | 'DEPARTURE' | 'CHECKIN_DEP' | 'CHECKIN_ARR' | 'CHECKIN_FINAL'
  | 'VACATED' | 'GOAROUND' | 'LANDING' | 'CHECKIN' | 'FREQ' | 'SAY_AGAIN';

export interface ClearanceData { dest: string; sid: string; altFt: number; squawk: string }

interface PendingRb { kind: Cmd['k']; wrong: Cmd; right: Cmd; until: number }

export class Pilot {
  freq: string | null = null;
  stage: Stage = 'PRE';
  clearance: ClearanceData | null = null;
  clearedTakeoff = false;
  clearedLand = false;
  crossings = new Set<string>();
  /** The pilot's own pending call and when it is made. */
  nextCall: { kind: RequestKind; at: number } | null = null;
  /** Last request made and how often it was repeated. */
  waitingFor: { kind: RequestKind; since: number; repeats: number; unit: string } | null = null;
  pendingRb: PendingRb | null = null;
  /** The pilot has made the initial call on the current frequency. */
  contacted = false;
  atisLetter = 'A';
  /** Departure holding point and runway assigned by ground / chosen by the pilot. */
  depHold: HoldPoint | null = null;
  depRwy: string | null = null;
  arrRwy: string | null = null;
  tobt = 0;
  startupDone = 0;
  vacatedAt = -1;
  parkedAt = -1;
  /** Seconds between ATC transmission end and the pilot's reaction. */
  private busyUntil = 0;
  private remindedFinal = false;
  private queue: { at: number; fn: () => void }[] = [];

  constructor(readonly ac: Aircraft, readonly world: World) {}

  get cs(): string { return this.ac.cs; }
  /** The pilot is still about to react to an instruction (read-back not yet given). */
  get reacting(): boolean { return this.queue.length > 0 || this.busyUntil > this.world.t; }
  get isDeparture(): boolean { return this.ac.fp.dep === this.world.airport.icao; }

  later(sec: number, fn: () => void): void { this.queue.push({ at: this.world.t + sec, fn }); }
  private reaction(min = 1.2, max = 3.5): number { return this.world.rng.range(min, max); }

  // ===================================================================== transmissions
  call(kind: RequestKind, msg: Phrase, withStation = true): void {
    if (!this.freq) return;
    const station = withStation ? this.world.unitCallsign(this.freq) : null;
    this.world.pilotTransmit(this, pilotCall(this.ac.fp, station, msg), kind);
    this.waitingFor = { kind, since: this.world.t, repeats: this.waitingFor?.kind === kind ? this.waitingFor.repeats + 1 : 0, unit: this.freq };
  }
  satisfied(kinds: RequestKind[]): void {
    if (this.waitingFor && kinds.includes(this.waitingFor.kind)) { this.world.resolveRequest(this.cs, this.waitingFor.kind); this.waitingFor = null; }
  }
  /** Switch to another unit and make the initial call there. */
  switchTo(unit: string): void {
    const old = this.freq;
    this.freq = null;
    this.contacted = false;
    if (old) this.world.resolveRequestsOf(this.cs);
    this.waitingFor = null;
    this.later(this.reaction(3, 7), () => { this.freq = unit; this.initialCall(); });
  }

  // ===================================================================== initial calls
  initialCall(): void {
    const ac = this.ac, w = this.world;
    const roles = w.unitRoles(this.freq!);
    switch (this.stage) {
      case 'PRE':
        this.call('CLEARANCE', P(`${w.standSay(ac.fp.stand)}, information ${this.atisLetter}, request clearance to ${w.destName(ac.fp.dest)}`,
          `${w.standSay(ac.fp.stand)}, information ${ALPHABET[this.atisLetter]}, request clearance to ${w.destName(ac.fp.dest)}`));
        return;
      case 'CLEARED': case 'PUSH_REQ': {
        this.stage = 'PUSH_REQ';
        const st = ac.stand;
        if (st && !st.push) this.call('STARTUP', P(`${w.standSay(ac.fp.stand)}, request start-up`));
        else this.call('PUSH', P(`${w.standSay(ac.fp.stand)}, ready for push-back`));
        return;
      }
      case 'STARTING': case 'TAXI_REQ':
        if (this.stage === 'TAXI_REQ') this.call('TAXI', P('ready for taxi'));
        return;
      case 'TAXIING': case 'AT_HOLD': {
        const h = this.depHold;
        if (roles.includes('TWR')) {
          if (this.stage === 'AT_HOLD' && h) this.call('DEPARTURE', P(`holding point ${h.name}, ready for departure`, `holding point ${spell(h.name)}, ready for departure`));
          else if (h) this.call('CHECKIN', P(`taxiing to holding point ${h.name}`, `taxiing to holding point ${spell(h.name)}`));
        } else if (this.stage === 'TAXIING') this.call('CHECKIN', P('taxiing'));
        else this.call('CHECKIN', P(`holding point ${h?.name ?? ''}`));
        return;
      }
      case 'CLIMB': case 'TAKEOFF': case 'ENROUTE': {
        const alt = this.levelText(ac.altFt, true), cleared = this.levelText(ac.tgtAltFt, false);
        const sid = this.clearance?.sid;
        const climbing = ac.tgtAltFt > ac.altFt + 200;
        this.call('CHECKIN_DEP', P(`passing ${alt.text}${climbing ? ` climbing ${cleared.text}` : ''}${sid && this.stage !== 'ENROUTE' ? `, ${sid}` : ''}`,
          `passing ${alt.spoken}${climbing ? ` climbing ${cleared.spoken}` : ''}${sid && this.stage !== 'ENROUTE' ? `, ${w.saySid(sid)}` : ''}`));
        return;
      }
      case 'ARRIVING': {
        if (roles.includes('TWR')) {
          const d = this.arrRwy ? distOnFinal(ac, w.airport.end(this.arrRwy)) : 0;
          this.stage = 'FINAL';
          this.call('CHECKIN_FINAL', P(`ILS runway ${this.arrRwy}, ${Math.max(1, Math.round(d))} miles`, `I L S runway ${sayRunway(this.arrRwy ?? '')}, ${spell(String(Math.max(1, Math.round(d))))} miles`));
          return;
        }
        const lvl = this.levelText(ac.altFt, true), tgt = this.levelText(ac.tgtAltFt, false);
        const desc = ac.tgtAltFt < ac.altFt - 300;
        this.call('CHECKIN_ARR', P(`${lvl.text}${desc ? ` descending ${tgt.text}` : ''}, information ${this.atisLetter}`,
          `${lvl.spoken}${desc ? ` descending ${tgt.spoken}` : ''}, information ${ALPHABET[this.atisLetter]}`));
        return;
      }
      case 'FINAL': {
        const d = this.arrRwy ? distOnFinal(ac, w.airport.end(this.arrRwy)) : 0;
        this.call('CHECKIN_FINAL', P(`ILS runway ${this.arrRwy}, ${Math.max(1, Math.round(d))} miles`, `I L S runway ${sayRunway(this.arrRwy ?? '')}, ${spell(String(Math.max(1, Math.round(d))))} miles`));
        return;
      }
      case 'LANDED': case 'TAXI_IN': {
        const ex = ac.exit?.name;
        if (roles.includes('GND') || roles.includes('APN')) {
          this.call('VACATED', P(`vacated runway ${this.arrRwy}${ex ? ` via ${ex}` : ''}${ac.fp.stand ? `, for ${w.standSay(ac.fp.stand)}` : ''}`,
            `vacated runway ${sayRunway(this.arrRwy ?? '')}${ex ? ` via ${spell(ex)}` : ''}${ac.fp.stand ? `, for ${w.standSay(ac.fp.stand)}` : ''}`));
        } else this.call('CHECKIN', P(`runway vacated${ex ? ` via ${ex}` : ''}`, `runway vacated${ex ? ` via ${spell(ex)}` : ''}`));
        return;
      }
      default:
    }
  }

  levelText(ft: number, current: boolean): Phrase {
    const w = this.world;
    const ta = w.meta.transitionAltFt;
    const fl = current ? this.ac.pressureAlt(w.weather.qnh) > w.tl * 100 - 50 : ft > ta + 50;
    const val = fl ? Math.round(this.ac.pressureAlt(w.weather.qnh) / 100) * 100 : Math.round(ft / 100) * 100;
    const v = current ? val : fl ? Math.round((ft + (1013.25 - w.weather.qnh) * 27.3) / 1000) * 1000 : Math.round(ft / 100) * 100;
    return P(showLevel(v, fl), sayLevel(v, fl));
  }

  // ===================================================================== receiving instructions
  /** Called when an ATC transmission addressed to this aircraft has ended on its frequency. */
  receive(cmds: Cmd[], unit: string, byPlayer: boolean): void {
    if (this.freq !== unit) return;
    const w = this.world;
    // immediate actions – no delay
    for (const c of cmds) {
      if (c.k === 'HOLD_POS') this.ac.holdPosition = true;
      if (c.k === 'CANCEL_TAKEOFF' && this.ac.gmode === 'TAKEOFF' && this.ac.onGround && this.ac.ias < this.ac.type.v2 - 30) this.abortTakeoff();
      if (c.k === 'GO_AROUND' && !this.ac.onGround && this.stage === 'FINAL') this.goAround(false);
    }
    // correction of an erroneous read-back?
    if (this.pendingRb && cmds.some(c => c.k === this.pendingRb!.kind)) {
      w.readbackCorrected(this.cs, true);
      this.pendingRb = null;
    } else if (this.pendingRb && cmds.some(c => c.k === 'RB_OK')) {
      w.readbackCorrected(this.cs, false);
      this.applyCmd(this.pendingRb.wrong, unit);
      this.pendingRb = null;
    }
    if (cmds.length === 1 && cmds[0]!.k === 'RB_OK') { this.onRbOk(); return; }
    // validity checks – "unable"
    const unable = cmds.map(c => this.check(c)).find(Boolean);
    const react = this.reaction();
    this.busyUntil = w.t + react + 2;
    this.later(react, () => {
      if (unable) {
        this.world.pilotTransmit(this, pilotCall(this.ac.fp, null, P(unable)), 'SAY_AGAIN');
        const ok = cmds.filter(c => !this.check(c));
        for (const c of ok) this.applyCmd(c, unit);
        return;
      }
      // read-back, possibly with an error
      let rbCmds = cmds;
      let err: PendingRb | null = null;
      if (w.rng.chance(w.options.readbackErrorRate) && byPlayer) {
        const idx = cmds.findIndex(c => ['ALT', 'HDG', 'SQUAWK', 'SPD', 'CLEARANCE'].includes(c.k));
        if (idx >= 0) {
          const wrong = this.corrupt(cmds[idx]!);
          if (wrong) {
            rbCmds = cmds.map((c, i) => (i === idx ? wrong : c));
            err = { kind: cmds[idx]!.k, wrong, right: cmds[idx]!, until: w.t + 20 };
          }
        }
      }
      const rb = readbackTransmission(this.ac.fp, rbCmds, w.phraseCtx());
      if (rb) w.pilotTransmit(this, rb, null, !!err);
      if (err) {
        err.until = w.t + 25; this.pendingRb = err; w.readbackErrorMade(this.cs, err.right, err.wrong);
        // the pilot believes he has his clearance – no further requests
        if (err.kind === 'CLEARANCE') this.satisfied(['CLEARANCE']);
      }
      for (const c of cmds) {
        if (err && c === err.right) continue; // executed when the error is corrected or confirmed
        this.applyCmd(c, unit);
      }
    });
  }

  private corrupt(c: Cmd): Cmd | null {
    const r = this.world.rng;
    switch (c.k) {
      case 'ALT': return { ...c, ft: c.ft + (r.chance(0.5) ? 1000 : -1000) * (c.ft > 5000 ? 1 : 1) };
      case 'HDG': return { ...c, hdg: norm360(c.hdg + r.pick([-20, 20, 30, -30])) || 360 };
      case 'SPD': return { ...c, kt: c.kt + r.pick([-20, 20]) };
      case 'SQUAWK': return { ...c, code: swapDigits(c.code, r.int(0, 2)) };
      case 'CLEARANCE': return r.chance(0.5) ? { ...c, squawk: swapDigits(c.squawk, r.int(0, 2)) } : { ...c, altFt: c.altFt + 1000 };
      default: return null;
    }
  }

  private check(c: Cmd): string | null {
    const ac = this.ac, w = this.world;
    switch (c.k) {
      case 'SPD': if (!ac.onGround && c.kt < (ac.locCap ? ac.type.vapp : ac.type.minClean - 20)) return `unable speed ${Math.round(c.kt)} knots`; return null;
      case 'ALT': if (c.ft > ac.type.ceilingFL * 100) return 'unable, level too high for our weight'; return null;
      case 'DIRECT': case 'HOLD_AT': if (!w.fixPos(c.fix)) return `say again, unable to find ${c.fix}`; return null;
      case 'TAXI': if (!w.planTaxi(this, c, true)) return 'unable, say again taxi route'; return null;
      case 'LINEUP': case 'TAKEOFF': if (!ac.onGround) return null; if (!this.clearance) return 'we have not received our clearance yet'; return null;
      case 'ILS': if (!w.airport.runwayEnds[c.rwy]?.meta.ils) return `unable, no ILS for runway ${c.rwy}`; return null;
      default: return null;
    }
  }

  private onRbOk(): void {
    if (this.stage === 'PRE' && this.clearance) {
      this.stage = 'CLEARED';
      this.satisfied(['CLEARANCE']);
    }
  }

  /** Executes an instruction. */
  applyCmd(c: Cmd, unit: string): void {
    const ac = this.ac, w = this.world;
    switch (c.k) {
      case 'CLEARANCE':
        this.clearance = { dest: c.dest, sid: c.sid, altFt: c.altFt, squawk: c.squawk };
        ac.squawk = c.squawk;
        if (!this.tobt) this.tobt = w.t + w.rng.range(60, 150);
        this.satisfied(['CLEARANCE']);
        return;
      case 'PUSH': case 'STARTUP':
        if (ac.gmode !== 'PARKED') return;
        if (this.stage === 'PRE' || this.stage === 'CLEARED' || this.stage === 'PUSH_REQ') {
          this.satisfied(['PUSH', 'STARTUP']);
          if (c.k === 'PUSH' && ac.stand?.push !== false) { w.startPushback(this, c.facing); this.stage = 'PUSHING'; }
          else { ac.gmode = 'STARTUP'; this.stage = 'STARTING'; this.startupDone = w.t + w.rng.range(45, 90); }
        }
        return;
      case 'TAXI':
        if (!w.planTaxi(this, c, false)) return;
        this.satisfied(['TAXI', 'VACATED']);
        if (this.isDeparture) this.stage = 'TAXIING';
        else { this.stage = 'TAXI_IN'; }
        ac.gmode = 'TAXI';
        return;
      case 'HOLD_POS': ac.holdPosition = true; return;
      case 'CONT_TAXI': ac.holdPosition = false; return;
      case 'CROSS':
        this.crossings.add(c.rwy);
        ac.clearStopsMatching(why => why === `RWY:${w.runwayIdOf(c.rwy)}`);
        ac.holdPosition = false;
        return;
      case 'HOLD_SHORT': w.addHoldShort(this, c.rwy); return;
      case 'LINEUP':
        if (ac.onGround && (this.stage === 'TAXIING' || this.stage === 'AT_HOLD')) { w.startLineup(this, c.rwy, c.hold); this.stage = 'LINEUP'; this.satisfied(['DEPARTURE', 'CHECKIN']); }
        return;
      case 'TAKEOFF':
        this.clearedTakeoff = true;
        this.satisfied(['DEPARTURE', 'CHECKIN']);
        if (ac.onGround && (this.stage === 'TAXIING' || this.stage === 'AT_HOLD')) { w.startLineup(this, c.rwy, undefined); this.stage = 'LINEUP'; }
        return;
      case 'CANCEL_TAKEOFF': this.clearedTakeoff = false; return;
      case 'LAND': this.clearedLand = true; this.satisfied(['CHECKIN_FINAL', 'LANDING']); return;
      case 'CONTINUE': this.satisfied(['CHECKIN_FINAL']); return;
      case 'GO_AROUND': if (!ac.onGround) this.goAround(false); return;
      case 'VACATE': ac.vacateVia = c.via; ac.exit = null; return;
      case 'HDG': if (!ac.onGround) { ac.flyHeading(w.magToTrue(c.hdg), c.turn); this.satisfied(['CHECKIN_DEP', 'CHECKIN_ARR', 'CHECKIN', 'GOAROUND']); } return;
      case 'ALT':
        ac.tgtAltFt = c.fl ? c.ft - (1013.25 - w.weather.qnh) * 27.3 : c.ft;
        this.satisfied(['CHECKIN_DEP', 'CHECKIN_ARR', 'CHECKIN', 'GOAROUND']);
        return;
      case 'SPD': ac.atcSpeed = c.kt; return;
      case 'SPD_OWN': ac.atcSpeed = null; return;
      case 'DIRECT': {
        // keep the remaining route after the fix
        const i = ac.route.indexOf(c.fix);
        ac.directTo(c.fix, i >= 0 ? ac.route.slice(i) : [c.fix]);
        this.satisfied(['CHECKIN_DEP', 'CHECKIN_ARR', 'CHECKIN', 'GOAROUND']);
        return;
      }
      case 'ILS': {
        const end = w.airport.runwayEnds[c.rwy];
        if (end) { ac.clearIls(end); this.arrRwy = c.rwy; }
        this.satisfied(['CHECKIN_ARR', 'CHECKIN']);
        return;
      }
      case 'HOLD_AT': {
        const h = w.meta.holdings.find(x => x.fix === c.fix);
        ac.directTo(c.fix);
        ac.hold = { fix: c.fix, inbound: w.magToTrue(h?.inboundCourse ?? 270), turn: h?.turn ?? 'R', phase: 'TO_FIX', legT: 0 };
        return;
      }
      case 'CONTACT':
        if (c.unit !== unit) this.switchTo(c.unit);
        return;
      case 'SQUAWK': ac.squawk = c.code; return;
      case 'IDENTIFIED': this.satisfied(['CHECKIN_DEP', 'CHECKIN_ARR', 'CHECKIN']); return;
      case 'RESUME_OWN': if (this.clearance) ac.directTo(ac.route[ac.legIdx] ?? ac.route[0] ?? '', ac.route.slice(ac.legIdx)); return;
      default:
    }
  }

  abortTakeoff(): void {
    this.ac.gmode = 'ROLLOUT';
    this.ac.exit = null;
    this.world.pilotTransmit(this, pilotCall(this.ac.fp, null, P('stopping')), null);
    this.stage = 'LANDED';
  }

  goAround(own: boolean): void {
    const ac = this.ac, w = this.world;
    ac.startGoAround(Math.max(3000, Math.round((w.meta.elevFt + 3000) / 100) * 100));
    this.clearedLand = false;
    this.stage = 'ARRIVING';
    this.remindedFinal = false;
    w.goAroundHappened(this, own);
    if (own) this.world.pilotTransmit(this, pilotCall(ac.fp, null, P('going around')), 'GOAROUND');
  }

  // ===================================================================== update
  update(_dt: number): void {
    const w = this.world, ac = this.ac;
    // queued actions
    if (this.queue.length) {
      const due = this.queue.filter(q => q.at <= w.t);
      if (due.length) { this.queue = this.queue.filter(q => q.at > w.t); for (const q of due) q.fn(); }
    }
    // erroneous read-back not corrected in time → the pilot flies the wrong value
    if (this.pendingRb && w.t > this.pendingRb.until) {
      w.readbackCorrected(this.cs, false);
      this.applyCmd(this.pendingRb.wrong, this.freq ?? '');
      this.pendingRb = null;
    }
    // own calls
    if (this.nextCall && w.t >= this.nextCall.at && this.freq) {
      this.nextCall = null;
      this.initialCall();
    }
    // repeat unanswered requests
    if (this.waitingFor && this.freq && w.t - this.waitingFor.since > 45 + this.waitingFor.repeats * 25 && w.t > this.busyUntil && !w.awaitingReaction(this.cs)) {
      if (this.waitingFor.repeats < 3) {
        w.unanswered(this.cs, this.waitingFor.kind, this.waitingFor.unit);
        this.initialCall();
      }
    }
    switch (this.stage) {
      case 'CLEARED':
        if (w.t >= this.tobt && this.freq) {
          const pushUnit = w.unitFor(ac.stand?.push === false ? 'GND' : 'APN') ?? w.unitFor('GND');
          if (pushUnit && pushUnit !== this.freq) { this.stage = 'PUSH_REQ'; this.switchTo(pushUnit); }
          else { this.stage = 'PUSH_REQ'; this.initialCall(); }
        }
        break;
      case 'PUSHING':
        if (ac.gmode === 'STARTUP') { this.stage = 'STARTING'; this.startupDone = w.t + w.rng.range(40, 80); }
        break;
      case 'STARTING':
        if (w.t >= this.startupDone) { ac.gmode = 'READY'; this.stage = 'TAXI_REQ'; this.initialCall(); }
        break;
      case 'TAXIING': {
        const st = ac.waitingAt();
        if (this.depHold && dist(ac.x, ac.y, w.airport.nodes[this.depHold.node]!.x, w.airport.nodes[this.depHold.node]!.y) < 8 && ac.stopped) {
          this.stage = 'AT_HOLD';
          if (this.freq && w.unitRoles(this.freq).includes('TWR')) this.initialCall();
        } else if (st && st.startsWith('RWY:') && this.freq && !this.waitingFor) {
          this.call('CHECKIN', P(`holding short runway ${st.slice(4).split('/')[0]}`, `holding short runway ${sayRunway(st.slice(4).split('/')[0] ?? '')}`), false);
        }
        break;
      }
      case 'AT_HOLD':
        if (this.clearedTakeoff && ac.gmode === 'TAXI') { w.startLineup(this, this.depRwy!, this.depHold?.name); this.stage = 'LINEUP'; }
        break;
      case 'LINEUP':
        if (ac.gmode === 'LINED_UP' && this.clearedTakeoff) { ac.startTakeoff(); this.stage = 'TAKEOFF'; w.takeoffStarted(this); }
        break;
      case 'TAKEOFF':
        if (!ac.onGround) { this.stage = 'CLIMB'; ac.airborneT = w.t; ac.tgtAltFt = this.clearance?.altFt ?? 4000; ac.phaseFlags.add('airborne'); }
        else if (ac.gmode === 'ROLLOUT') this.stage = 'LANDED';
        break;
      case 'CLIMB': {
        const sid = w.sidOf(this.clearance?.sid);
        const agl = ac.altFt - w.meta.elevFt;
        if (!ac.phaseFlags.has('sidTurn') && agl > (sid?.turnAltFt ?? 1500) - w.meta.elevFt) {
          ac.phaseFlags.add('sidTurn');
          if (ac.amode === 'HDG' && ac.tgtHdg !== null && Math.abs(ac.tgtHdg - (w.airport.runwayEnds[this.depRwy ?? '']?.hdg ?? ac.tgtHdg)) < 1 && sid) ac.directTo(sid.legs[0]!.fix, sid.legs.map(l => l.fix));
        }
        if (dist(ac.x, ac.y, 0, 0) > 25 * NM || ac.altFt > 9000) this.stage = 'ENROUTE';
        break;
      }
      case 'ARRIVING': case 'FINAL': {
        if (!ac.onGround && this.arrRwy) {
          const end = w.airport.end(this.arrRwy);
          const d = distOnFinal(ac, end);
          if (ac.locCap && d < 2.2 && !this.clearedLand && !this.remindedFinal && this.freq) {
            this.remindedFinal = true;
            this.call('LANDING', P(`final runway ${this.arrRwy}`, `final runway ${sayRunway(this.arrRwy)}`), false);
          }
          if (ac.gsCap && d < 0.6 && !this.clearedLand) this.goAround(true);
        }
        if (ac.onGround && ac.gmode === 'ROLLOUT') { this.stage = 'LANDED'; ac.landedT = w.t; }
        break;
      }
      case 'LANDED':
        if (ac.gmode === 'VACATED' && this.vacatedAt < 0) { this.vacatedAt = w.t; w.vacated(this); }
        if (this.vacatedAt > 0 && w.t - this.vacatedAt > 25 && this.freq && w.unitRoles(this.freq).includes('TWR') && !this.waitingFor) {
          this.call('CHECKIN', P(`runway vacated${ac.exit ? ` via ${ac.exit.name}` : ''}`, `runway vacated${ac.exit ? ` via ${spell(ac.exit.name)}` : ''}`), false);
        }
        break;
      case 'TAXI_IN':
        if (ac.atEnd && ac.stopped && ac.fp.stand && w.airport.stands[ac.fp.stand] && dist(ac.x, ac.y, w.airport.nodes[w.airport.stands[ac.fp.stand]!.node]!.x, w.airport.nodes[w.airport.stands[ac.fp.stand]!.node]!.y) < 10) {
          this.stage = 'PARKED'; ac.gmode = 'PARKED'; this.parkedAt = w.t; w.parked(this);
        }
        break;
      default:
    }
  }
}

function swapDigits(code: string, i: number): string {
  const a = code.split('');
  [a[i], a[i + 1]] = [a[i + 1]!, a[i]!];
  const s = a.join('');
  return s === code ? code.slice(0, 3) + String((Number(code[3]) + 1) % 8) : s;
}
